import { useEffect, useState } from "react";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { Ch } from "@/components/Fa";
import { Tile } from "@/components/Tile";

const C = { positive: "var(--mark-pos)", neutral: "var(--mark-neu)", negative: "var(--mark-neg)" } as const;
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };
const MT_LABEL: Record<string, string> = { news_online: "Online", print: "Cetak", tv: "TV", radio: "Radio" };
const TIER_LABEL: Record<string, string> = { "1": "Tier 1 · Nasional", "2": "Tier 2 · Regional", "3": "Tier 3 · Lokal" };
const TIER_SHORT: Record<string, string> = { "1": "T1", "2": "T2", "3": "T3" };
const nf = (x: number) => Intl.NumberFormat("id-ID", { notation: "compact" }).format(x ?? 0);
const fmtRp = (n: number | null | undefined) => (n == null ? "—" : "Rp " + Number(n).toLocaleString("id-ID"));

export default function TopMediaPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [arts, setArts] = useState<Record<string, any[]>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);
  async function load(s2?: string, e2?: string) {
    const r = await fetch(`/api/v1/media/top?start=${s2 ?? start}&end=${e2 ?? end}`);
    if (r.status === 401) { location.href = "/login"; return; }
    setRows((await r.json()).data ?? []); setOpen(null); setArts({});
  }
  useEffect(() => { load(); }, []);
  async function toggle(sourceId: string) {
    if (open === sourceId) { setOpen(null); return; }
    setOpen(sourceId);
    if (!arts[sourceId]) {
      const r = await fetch(`/api/v1/mentions?source=${encodeURIComponent(sourceId)}&start=${start}&end=${end}&page=1&pageSize=50&vendor=digivla`);
      const j = await r.json();
      setArts((p) => ({ ...p, [sourceId]: j.data ?? [] }));
    }
  }
  const totalArticles = rows.reduce((a, r) => a + (r.articles ?? 0), 0);
  const totalReach = rows.reduce((a, r) => a + (r.reach ?? 0), 0);
  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Top Media</h1>
          <div className="avio-t-sm avio-muted">Media massa · {start} → {end} (WIB) · klik baris untuk melihat artikelnya</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="media" />
      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-newspaper" label="Media terpantau" value={rows.length} sub="outlet" />
        <Tile c={3} icon="fa-solid fa-file-lines" label="Total artikel" value={totalArticles} sub="dari media ini" />
        <Tile c={5} icon="fa-solid fa-eye" label="Total reach" value={totalReach} sub="jangkauan estimasi" />
      </div>
      <div className="avio-tablewrap">
      <DataGrid
        cols={[
          { id: "no", header: "#", width: "max", cell: (r: any) => rows.indexOf(r) + 1 },
          { id: "media", header: "Media (klik = artikel)", width: "min:220", sortable: true, accessor: (r: any) => r.media, cell: (r: any) => (
            <span style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
              <i className={open === r.sourceId ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"} style={{ fontSize: 10, color: "var(--color-text-muted)" }} />
              <span className="avio-ava" style={{ background: "var(--color-surface-sunken)", color: "var(--color-text-secondary)" }}>
                <i className={r.sourceType === "print" ? "fa-regular fa-newspaper" : r.sourceType === "tv" ? "fa-solid fa-tv" : r.sourceType === "radio" ? "fa-solid fa-radio" : "fa-solid fa-globe"} />
              </span>
              <span style={{ minWidth: 0 }}>
                <b style={{ display: "block" }}>{r.media}</b>
                <span className="avio-sub">{r.domain ?? "—"}{MT_LABEL[r.sourceType] ? ` · ${MT_LABEL[r.sourceType]}` : ""}</span>
              </span>
            </span>) },
          { id: "art", header: "Artikel", width: "max", sortable: true, align: "right", accessor: (r: any) => r.articles, cell: (r: any) => <b>{r.articles}</b> },
          { id: "tier", header: "Tier", width: "min:80", sortable: true, accessor: (r: any) => r.tier ?? 99, cell: (r: any) => {
            const t = r.tier != null ? String(r.tier) : null;
            return t
              ? <span className="avio-chip" data-tone={t === "1" ? "brand" : "soft"} title={TIER_LABEL[t] ?? `Tier ${t}`}><span className="avio-chip__label">{TIER_SHORT[t] ?? `T${t}`}</span></span>
              : <span className="avio-muted">—</span>;
          } },
          { id: "tone", header: "Komposisi tone", width: "min:120", cell: (r: any) => (
            <span style={{ display: "grid", gap: 3, minWidth: 100 }}>
              <span className="avio-prow__track">
                {r.positive > 0 && <span className="avio-prow__fill" style={{ flex: r.positive, background: C.positive }} />}
                {r.neutral > 0 && <span className="avio-prow__fill" style={{ flex: r.neutral, background: C.neutral }} />}
                {r.negative > 0 && <span className="avio-prow__fill" style={{ flex: r.negative, background: C.negative }} />}
              </span>
              <span className="avio-sub" style={{ fontVariantNumeric: "tabular-nums" }}>
                <span style={{ color: "var(--sent-pos)" }}>+{r.positive}</span> · {r.neutral} · <span style={{ color: "var(--sent-neg)" }}>-{r.negative}</span>
              </span>
            </span>) },
          { id: "reach", header: "Reach", width: "max", sortable: true, align: "right", accessor: (r: any) => r.reach ?? 0, cell: (r: any) => nf(r.reach) },
          { id: "advalue", header: "Ad Value", width: "max", sortable: true, align: "right", accessor: (r: any) => r.adValue ?? 0, cell: (r: any) => fmtRp(r.adValue) },
          { id: "newsvalue", header: "News Value", width: "max", sortable: true, align: "right", accessor: (r: any) => r.newsValue ?? 0, cell: (r: any) => (
            <span style={{ color: (r.newsValue ?? 0) < 0 ? "var(--sent-neg)" : undefined }}>{fmtRp(r.newsValue)}</span>) },
        ] as GridCol<any>[]}
        data={rows}
        onRowClick={(r) => toggle(r.sourceId)}
        expandedRender={(r) => open === r.sourceId ? (
          !arts[r.sourceId] ? <span>memuat…</span> : <>{arts[r.sourceId].map((a: any) => (
            <a key={a.id} className="avio-item" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDetailId(a.id); }} style={{ paddingLeft: 0, paddingRight: 0 }}>
              <span className="avio-item__main">
                <span className="avio-item__title">{(a.title || "(tanpa judul)").slice(0, 120)}</span>
                <span className="avio-item__meta">
                  {new Date(new Date(a.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(5, 16).replace("T", " ")} ·
                  <Ch t={a.sourceType} /> {MT_LABEL[a.sourceType] ?? a.sourceType}
                  {a.journalist ? ` · ${a.journalist}` : ""}
                </span>
              </span>
              {a.sentimentLabel && <span className="avio-chip" data-sentiment={a.sentimentLabel}><span className="avio-chip__label">{TONE_ID[a.sentimentLabel] ?? a.sentimentLabel}</span></span>}
            </a>
          ))}</>
        ) : null}
        emptyText="Belum ada data media — jalankan sync Media dulu."
        minTableWidth={900}
      />
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
